import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import ComponentWrapper from '../../common/ContentWrapper';
import Card from './Card';
import type FlagshipHotel from '../../../../types/FlagshipHotel';
import Carousel from '../../common/Carousel';
import analytics from '../../../core/analytics/analytics';
import { Category, PromotionType } from '../../../core/constants';
import useStartPrices from '../../../core/hotel/useStartPrices';
import { flagshipHotelsTestIds as testIds } from './testIds';

type Props = {
  flagshipHotels: FlagshipHotel[];
  checkInDate?: string;
  checkOutDate?: string;
  guestPerRoom?: string;
  roomCount?: number;
};

const FlagshipHotels = ({
  flagshipHotels,
  checkInDate = undefined,
  checkOutDate = undefined,
  guestPerRoom = undefined,
  roomCount = undefined,
}: Props) => {
  const { t } = useTranslation('home');
  const router = useRouter();
  const { locale } = router;
  const maxSlidesToShow = 4;
  const numOfFlagshipHotels = flagshipHotels.length;
  const settings = {
    className: '-mx-2', // remove spaces at the far left/right ends from slider container
    cardSpacing: 'px-2', // add spaces at both of the sides of cards
    infinite: numOfFlagshipHotels > maxSlidesToShow,
    slidesToShow: 4,
    draggable: false,
    prevBtnStyle: '-left-3 top-24 card-carousel-arrow-btn',
    nextBtnStyle: '-right-3 top-24 card-carousel-arrow-btn',
    prevArrowStyle: 'stroke-2',
    nextArrowStyle: 'stroke-2',
    responsive: [
      {
        breakpoint: 1536,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1.1,
          infinite: false,
          arrows: false,
          draggable: true,
        },
      },
    ],
  };

  const events = {
    onClick: (hotel: FlagshipHotel) => {
      analytics.google.ecommerce.track.promotion.view(
        {
          promotion_name: PromotionType.FLAGSHIP_HOTELS,
          promotion_id: hotel.id,
        },
        [
          {
            item_id: hotel?.id || '',
            item_name: hotel?.name || '',
            item_category: Category.HOTEL,
          },
        ]
      );
    },
  };

  const hotelIds = useMemo(() => flagshipHotels?.map((hotel) => hotel.id), [flagshipHotels]);
  const { startPricesList, isLoading } = useStartPrices({
    propertyIds: hotelIds,
    date: checkInDate,
    locale,
  });

  return (
    <ComponentWrapper className="pt-12 sm:pt-16">
      <h2 data-testid={testIds.section_title} className="app-section-title">
        {t('flagship.flagship_title')}
      </h2>

      <div className="-mr-2 sm:mr-0">
        <Carousel
          settings={settings}
          items={flagshipHotels}
          renderItem={(hotel, prefetchImages) => {
            const startPrices = startPricesList?.find((price) => price.propertyId === hotel.id);

            return (
              <Card
                key={hotel.id}
                url={`/h/${hotel?.id}?${new URLSearchParams(
                  Object.fromEntries(
                    Object.entries({
                      checkInDate,
                      checkOutDate,
                      guestPerRoom,
                      roomCount,
                    }).filter(([, value]) => value)
                  ) as Record<string, string>
                ).toString()}`}
                roomPhotoUrl={hotel.image?.url}
                roomPhotoAltText={hotel.image?.alt}
                startPrices={startPrices}
                isStartPriceLoading={isLoading}
                webCampaigns={hotel.webCampaigns || []}
                name={hotel.name}
                address={hotel.address}
                accessSimple={hotel.accessSimple}
                hotelRating={hotel.hotelRating}
                onClick={() => events.onClick(hotel)}
                prefetchImage={prefetchImages}
              />
            );
          }}
        />
      </div>
    </ComponentWrapper>
  );
};

export default FlagshipHotels;
